import React from 'react';
import clsx from 'clsx';

import Button from '@components/button/Button';
import { SelectedFileItem } from '@features/lead-management/types/leadFileUpload';

import LeadFileCard from '../LeadFileCard';

import { UploadStatus } from '../../constants/leadFileUpload';

type NotesInputProps = {
  noteText: string;
  setNoteText: (text: string) => void;
  hasInteracted: boolean;
  setHasInteracted: (val: boolean) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  newFiles: any[];
  addFiles: (files: FileList) => void;
  removeItem: (file: SelectedFileItem) => void;
  handleAttachFile: () => void;
  handleCancel: () => void;
  handleSave: () => void;
};

const NotesInput: React.FC<NotesInputProps> = ({
  noteText,
  setNoteText,
  hasInteracted,
  setHasInteracted,
  textareaRef,
  fileInputRef,
  newFiles,
  addFiles,
  removeItem,
  handleAttachFile,
  handleCancel,
  handleSave
}) => {
  const [error, setError] = React.useState<string | null>(null);

  const isUploading = newFiles.some(
    (file) => file.status === UploadStatus.IN_PROGRESS
  );
  // Text is optional once an attachment is uploaded.
  const hasUploadedFile = newFiles.some(
    (file) => file.status === UploadStatus.COMPLETED
  );
  const textLength = noteText.trim().length;
  const canSave = !isUploading && (textLength > 0 || hasUploadedFile);

  const onSave = () => {
    if (textLength > 0 && (textLength < 3 || textLength > 500)) {
      setError('Notes should be between 3 and 500 characters');
      return;
    }
    handleSave();
  };

  return (
    <div className="flex flex-col">
      <div className="relative flex h-[155px] w-full flex-col justify-between rounded-xl border border-[#00A657] bg-[#F9F9F9] p-4">
        <textarea
          value={noteText}
          onChange={(e) => {
            setNoteText(e.target.value);
            setHasInteracted(true);
            if (error) setError(null);
          }}
          placeholder="What's this note about?"
          rows={4}
          ref={textareaRef}
          className={clsx(
            'w-full resize-none border-0 bg-transparent',
            "font-['DM_Sans'] text-[16px] leading-[1.302] text-[#222222]",
            'placeholder:text-gray-500 focus:outline-none'
          )}
          style={{ fontFamily: 'DM Sans' }}
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => {
            const { files } = e.target;
            if (files && files.length > 0) addFiles(files);
            // reset input to allow re-uploading same file name
            e.currentTarget.value = '';
          }}
          className="hidden"
        />
        <div className="flex items-center justify-between">
          <Button
            handleClick={handleAttachFile}
            label="Attach File"
            variant="transparent"
            labelStyle="!text-darkGrey !text-[14px]"
            className={clsx(
              'flex items-center gap-2 rounded-lg bg-transparent p-2',
              "font-['DM_Sans'] text-[14px] font-medium leading-[1.571]",
              'hover:bg-gray-50 [&>*]:!text-black'
            )}
            leftIconName="attachment-grey"
          />
          {hasInteracted && (
            <div className="flex items-center gap-6">
              <Button
                variant="secondary"
                handleClick={handleCancel}
                label="Cancel"
                className={clsx(
                  'h-9 rounded-lg border border-[#00A657] bg-[#EDFFF7]',
                  "px-4 py-2.5 font-['DM_Sans'] text-[14px] font-medium",
                  'leading-[1.571] text-[#00A657] hover:bg-green-50'
                )}
              />
              <Button
                handleClick={onSave}
                label="Save"
                className={clsx(
                  'h-9 w-[78px] rounded-lg bg-[#00A657]',
                  'text-[14px] font-medium leading-[1.253]',
                  'text-white hover:bg-green-700'
                )}
                disabled={!canSave}
              />
            </div>
          )}
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      {newFiles.length > 0 && (
        <div className="mt-2 w-fit min-w-[50%]">
          <LeadFileCard files={newFiles} onRemove={removeItem} />
        </div>
      )}
    </div>
  );
};

export default NotesInput;
